import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { type TApiServiceError } from "@/api/apiService";
import { type TDealNetworkConnectRequest } from "@/api/dealNetwork";
import { Button, Card, CardContent, RouteLink, Typography } from "@/components/ui";
import { type TApiResponse } from "@/types/api";
import { type TAsyncTuple } from "@/utils/async";
import { extractErrorMessage } from "@/utils/errorUtils";

type TConfirmRequestFn = (
  connectRequestId: string
) => Promise<TAsyncTuple<TApiServiceError, TApiResponse<TDealNetworkConnectRequest>>>;

type TConnectRequestConfirmationPageProps = {
  connectRequestId?: string;
  confirmRequest: TConfirmRequestFn;
  fallbackErrorMessage?: string;
  missingIdMessage?: string;
};

const DEFAULT_FALLBACK_ERROR_MESSAGE =
  "We couldn't confirm this introduction right now. Please try again in a moment or request a new link.";
const DEFAULT_MISSING_ID_MESSAGE =
  "This introduction link is missing the request identifier. Please ask the Fundy team for a new invitation.";

export function ConnectRequestConfirmationPage({
  connectRequestId,
  confirmRequest,
  fallbackErrorMessage = DEFAULT_FALLBACK_ERROR_MESSAGE,
  missingIdMessage = DEFAULT_MISSING_ID_MESSAGE,
}: TConnectRequestConfirmationPageProps) {
  const [introduction, setIntroduction] = useState<TDealNetworkConnectRequest | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasConfirmed, setHasConfirmed] = useState(false);

  useEffect(() => {
    if (!connectRequestId) {
      setErrorMessage(missingIdMessage);
    }
  }, [connectRequestId, missingIdMessage]);

  // Confirmation is an explicit user action — never fired on page load.
  // Email scanners and link previews fetch these URLs, and confirming on
  // mount would accept introductions nobody reviewed (and double-send the
  // notification emails under StrictMode's double effects).
  const handleConfirm = async () => {
    if (!connectRequestId || isLoading) {
      return;
    }

    setHasConfirmed(true);
    setIsLoading(true);
    setErrorMessage(null);
    setIntroduction(null);

    const response = await confirmRequest(connectRequestId);

    if (response.error || !response.data?.data) {
      const message = (response.error ? await extractErrorMessage(response.error) : null) ?? fallbackErrorMessage;
      setErrorMessage(message);
      setIsLoading(false);
      return;
    }

    setIntroduction(response.data.data);
    setIsLoading(false);
  };

  const investorName = introduction?.investor_company?.name ?? "the investor company";
  const fundraiserName = introduction?.fundraiser_company?.name ?? "the fundraiser company";
  const formattedStatus = introduction?.status?.name
    ? introduction.status.name.charAt(0).toUpperCase() + introduction.status.name.slice(1)
    : null;

  const showSuccess = Boolean(introduction);
  const showError = Boolean(errorMessage);
  const showReview = !hasConfirmed && !showError && Boolean(connectRequestId);
  const canRetry = Boolean(connectRequestId) && showError;

  return (
    <article className="relative min-h-[calc(100vh-var(--footer-height))] overflow-hidden bg-gray-950 text-white">
      <div className="absolute inset-0">
        <div className="h-full w-full bg-gradient-to-b from-gray-900 via-slate-950 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(37,99,235,0.35),_transparent_65%)]" />
      </div>

      <section className="relative z-10 mx-auto flex w-full max-w-3xl flex-col px-4 pb-16 pt-[calc(var(--navbar-height)+2.5rem)]">
        <Card className="w-full border-border/20 bg-card/95 text-foreground shadow-2xl backdrop-blur">
          <CardContent className="space-y-8 p-8 md:p-10">
            {showReview && !isLoading ? (
              <div className="space-y-6 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                  <CheckCircle2
                    className="h-10 w-10 text-primary"
                    aria-hidden="true"
                  />
                </div>
                <div className="space-y-3">
                  <Typography variant="h3" className="text-foreground">
                    Confirm this introduction
                  </Typography>
                  <Typography variant="p" affects="muted" className="text-base text-muted-foreground md:text-lg">
                    A founder has asked Fundy to introduce you. If you accept, we&apos;ll email an introduction
                    to both of you so you can take the conversation forward.
                  </Typography>
                  <Typography variant="p" affects="muted" className="text-sm text-muted-foreground">
                    Not expecting this? You can simply close this page — nothing happens until you accept.
                  </Typography>
                </div>
                <Button
                  size="lg"
                  onClick={() => void handleConfirm()}
                  className="w-full justify-center md:w-auto"
                >
                  Accept introduction
                </Button>
              </div>
            ) : null}

            {isLoading ? (
              <div className="flex flex-col items-center gap-4 text-center">
                <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden="true" />
                <Typography variant="h3" className="text-foreground">
                  Confirming your introduction
                </Typography>
                <Typography variant="p" affects="muted" className="text-base text-muted-foreground md:text-lg">
                  Hang tight while we confirm your introduction request.
                </Typography>
              </div>
            ) : null}

            {!isLoading && showSuccess ? (
              <div className="space-y-6 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
                  <CheckCircle2
                    className="h-10 w-10 text-emerald-600"
                    aria-hidden="true"
                  />
                </div>
                <div className="space-y-3">
                  <Typography variant="h3" className="text-foreground">
                    Congratulations!
                  </Typography>
                  <Typography variant="p" affects="lead" className="text-foreground">
                    {investorName} has been introduced to {fundraiserName}.
                  </Typography>
                </div>

                <div className="grid gap-4 rounded-2xl border border-border bg-muted p-6 text-left text-foreground">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Investor company</p>
                    <p className="text-base font-semibold text-foreground">{investorName}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Fundraiser company</p>
                    <p className="text-base font-semibold text-foreground">{fundraiserName}</p>
                  </div>
                  {formattedStatus ? (
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Status</p>
                      <p className="text-base font-semibold text-emerald-600">{formattedStatus}</p>
                    </div>
                  ) : null}
                </div>

                <div className="space-y-2 text-left text-foreground">
                  <Typography variant="p" affects="withoutPMargin" className="text-base">
                    Check your email inbox for an email sent to you both.
                  </Typography>
                  <Typography
                    variant="p"
                    affects="withoutPMargin"
                    className="text-base"
                  >
                    Thank you
                  </Typography>
                  <Typography variant="p" affects="withoutPMargin" className="text-base font-semibold text-foreground">
                    The Fundy Team
                  </Typography>
                </div>
              </div>
            ) : null}

            {!isLoading && showError ? (
              <div className="space-y-4 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-50">
                  <AlertTriangle
                    className="h-10 w-10 text-amber-600"
                    aria-hidden="true"
                  />
                </div>
                <Typography variant="h3" className="text-foreground">
                  We couldn&apos;t confirm the introduction
                </Typography>
                <Typography variant="p" affects="muted" className="text-base text-muted-foreground">
                  {errorMessage}
                </Typography>
                {canRetry ? (
                  <Button
                    onClick={() => void handleConfirm()}
                    disabled={isLoading}
                    className="mt-2"
                  >
                    Try again
                  </Button>
                ) : null}
              </div>
            ) : null}

            <div className="flex justify-center">
              <RouteLink to="/">
                <Button
                  size="lg"
                  className="w-full justify-center md:w-auto"
                >
                  Go to Fundy home
                </Button>
              </RouteLink>
            </div>
          </CardContent>
        </Card>
      </section>
    </article>
  );
}
